import { Theme, useTheme } from "./Common/Composables/theme";
// Plugins and filters
import i18n, { setI18nLanguage } from "@Plugins/localization";
// Modules
import mountEventScheduleModule from "./Modules/Event/Schedule/main";
import { mountTemplates } from "./Modules/Templates/template-loading";
import { ComponentLoadingParams, mountComponents } from "./Common/Helpers/component-loader";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import { loadConfiguration } from "@Helpers/configuration";
import { loadRecaptchaConfiguration } from "@Helpers/recaptcha";
import { createPinia } from "pinia";
import router from "./Modules/Event/Registrations/router";

import "./cms.css";
import { EntryPoint, loadSentry } from "./Plugins/sentry";
import { ConsentSubject, registerConsentCallback } from "./Modules/Consent/consent";

window.addEventListener("DOMContentLoaded", () => {
    loadConfiguration();
    loadRecaptchaConfiguration();

    const { setTheme } = useTheme();
    setTheme(Theme.CMS);

    const vueConfig = document.querySelector("#vue-config");
    if (vueConfig) {
        const locale = vueConfig.getAttribute("data-locale");
        if (locale) {
            // Keep the server-rendered <html lang> (URL segment) for SEO; only sync Vue i18n messages.
            setI18nLanguage(locale, { syncHtmlLang: false });
        }
    }

    const pinia = createPinia();
    const queryClient = new QueryClient();

    registerConsentCallback(ConsentSubject.Sentry, () => loadSentry(EntryPoint.CMS));

    /**
     * Load the following components when detected in DOM
     */
    const componentList = {
        "calendar-link": {
            plugins: [i18n],
            component: () => import("./Common/Components/Buttons/CalendarLink.vue"),
        },
        "past-events-list": {
            plugins: [i18n],
            component: () => import("./Modules/Event/Calendar/Views/PastEventsList.vue"),
        },
        "payment-tip": {
            plugins: [i18n],
            component: () => import("./Modules/Payment/Tip/Views/Tip.vue"),
        },
        "member-my-memberships": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Member/MyMemberships/Views/MyMemberships.vue"),
        },
        "family-details": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Family/Views/FamilyDetails.vue"),
        },
        "member-organization-detail": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Member/OrganizationManagement/Views/OrganizationDetail.vue"),
        },
        "member-organization-edit": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Member/OrganizationManagement/Views/OrganizationEdit.vue"),
        },
        "event-registration-list": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Event/Registrations/Views/RegistrationList.vue"),
        },
        "event-registrations-payment": {
            plugins: [i18n],
            component: () => import("./Modules/Event/Registrations/Views/RegistrationPayment.vue"),
        },
        "event-registration-form": {
            plugins: [i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Event/Registrations/Views/RegistrationForm.vue"),
        },
        "event-registration-transactions": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            router,
            component: () => import("./Modules/Event/Registrations/Views/RegistrationRouter.vue"),
        },
        "event-registration-detail": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Event/Registrations/Views/RegistrationDetail.vue"),
        },
        "cart-identity": {
            plugins: [i18n],
            component: () => import("./Modules/Cart/Views/CartIdentity.vue"),
        },
        "cookie-consent": {
            plugins: [i18n],
            component: () => import("./Modules/Consent/Views/CookieConsentModal.vue"),
        },
        "floating-elements-queue": {
            plugins: [],
            component: () => import("./Common/Components/Notice/FloatingElementsQueue.vue"),
        },
        "stripe-payment": {
            plugins: [i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Payment/Stripe/Views/StripePayment.vue"),
        },
        "shop-product-list": {
            plugins: [pinia, i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/ShopProduct/Views/ProductList.vue"),
        },
        "form-multi-select-input": {
            plugins: [i18n],
            component: () => import("./Common/Components/Input/FormMultiSelectInput.vue"),
        },
        "form-date-picker-input": {
            plugins: [i18n],
            component: () => import("./Common/Components/Input/FormDatePickerInput.vue"),
        },
        "crm-object-workflow-actions": {
            plugins: [],
            component: () => import("./Modules/CRM/Field/Object/CrmObjectWorkflowActions.vue"),
        },
        "login-form": {
            plugins: [i18n, [VueQueryPlugin, { queryClient }]],
            component: () => import("./Modules/Login/Views/LoginForm.vue"),
        },
    } satisfies Record<string, ComponentLoadingParams>;

    mountComponents(componentList);

    document.addEventListener("reload-components", () => {
        console.log("Reloading components");
        mountComponents(componentList);
    });

    mountEventScheduleModule();

    mountTemplates();
});
